import SearchIcon from "@mui/icons-material/Search";
import { FormControl, InputAdornment, MenuItem } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { GlobalPlanInterface, GlobalPropertyInterface, GlobalUserInterface } from "Interface/interface";
import { ReactComponent as ArrowDown } from "assets/ArrowDown_GlobalSearch.svg";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import _, { isEmpty, isNull, isUndefined } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, allPlansType, DEFAULT_CORP_INNCODE, minSearchStrLength } from "utils/CommonConstants";
import { getHltInncodes, getUserHltId } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { ReactComponent as Recent } from "../../../assets/Recent.svg";
import {
  clearInnCodeSearch,
  clearPlanSearch,
  clearSearch,
  fetchGlobalInncodes,
  fetchGlobalPlansAsync,
  fetchGlobalUser,
  fetchInncodeInfo,
  fetchMultiTargeted,
  fetchTemplatePropertyPlansAsync,
  setCategory,
  setPlanOwner,
  setPropertyPlanList,
  setSearchLoadingCount,
  setTemplatePlanList,
  setUserList
} from "./GlobalSearch.slice";
import "./index.scss";

function categoryData(isConsultant = false, isCorp = false, isAbovePropertyUser = false) {
  if (isCorp || isConsultant) {
    return ["Property", "Member", "Library Plan"];
  } else if (isAbovePropertyUser) {
    return ["Property", "Member", "Library Plan", "Template Plan", "Property Plan"];
  }
  return ["Library Plan", "Member", "Property Plan"];
}

interface CategoryInterface {
  [key: string]: GlobalUserInterface[] | GlobalPlanInterface[] | GlobalPropertyInterface[];
}

const GlobalSearch = () => {
  const translate = useTranslate();
  const listInnerRef = useRef();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const innCode = useAppSelector((state: any) => state.app.innCode);

  const globalUserDetail: GlobalUserInterface[] = useAppSelector((state) => state.globalSearch.globalUserDetail);
  const {
    totalGlobalUser,
    globalUserListLoading,
    category,
    globalPlanList,
    globalInncodeList,
    templatePlanList,
    propertyPlanList,
    planOwnerList,
    searchLoading
  } = useAppSelector((state) => state.globalSearch);
  const { isConsultant, isCorp, isAbovePropertyUser } = useAppSelector((state) => state.app.loggedInUserDetails);

  const recentFirstElement = () => {
    const recentSearch = localStorage.getItem("recentSearch");
    if (recentSearch && isEmpty(searchText)) {
      const parsedUser = JSON.parse(recentSearch);
      return parsedUser[category][0];
    }
  };

  const resultPerPage = 100;
  const isCategoryUser = category === "Member";
  const isCategoryProperty = category === "Property";
  const isCategoryPlan = category === "Plan" || category === "Library Plan";
  const isTemplatePlan = category === "Template Plan";
  const isPropertyPlan = category === "Property Plan";
  const isSearchTextEmpty = searchText === "";

  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);

  const associatedCodes = localStorage.getItem("hltInnCodes");

  const isHotelUser = !isCorp && !isConsultant && !isAbovePropertyUser;
  const selectedInncode = isHotelUser ? innCode : isAbovePropertyUser ? associatedCodes : DEFAULT_CORP_INNCODE;
  // Function to handle input change in a Autocomplete
  const handleInputChange = (event: React.SyntheticEvent, value: string, reason: string) => {
    if (!value || value.length < 3) {
      dispatch(clearSearch());
    }
    resetAllData();
    setSearchText(value);
  };

  // This useEffect hook initializes or resets the recent search data stored in localStorage
  useEffect(() => {
    const globalSearchData = localStorage.getItem("recentSearch");
    const defaultRecentSearch = {
      Member: [],
      Property: [],
      "Library Plan": [],
      "Template Plan": [],
      "Property Plan": []
    };
    if (!isImpersonated && !isHotelUser) {
      if (isNull(globalSearchData)) {
        localStorage.setItem("recentSearch", JSON.stringify(defaultRecentSearch));
      } else {
        const parcedData = JSON.parse(globalSearchData);
        const updatedRecentSearch = {
          ...defaultRecentSearch,
          ...parcedData
        };
        localStorage.setItem("recentSearch", JSON.stringify(updatedRecentSearch));
      }
    }
    return () => {
      resetAllData();
    };
  }, []);

  // Property plans across different properties can share a name, so identity is name + innCode
  const isSamePropertyPlan = (a: any, b: any) => a?.name === b?.name && a?.innCode === b?.innCode;

  /* This handleCategoryChange trigger when change the category it reset all the data */
  const handleCategoryChange = (event: SelectChangeEvent) => {
    resetAllData();
    window.stop();
    dispatch(setCategory(event.target.value));
  };

  useEffect(() => {
    if (!isCorp && !isAbovePropertyUser && !isConsultant) {
      dispatch(setCategory("Library Plan"));
    } else {
      dispatch(setCategory("Property"));
    }
  }, [isCorp, isAbovePropertyUser, isConsultant]);

  const findDuplicate = (category: string, globalSearchData: CategoryInterface, item: any) => {
    const selectedKey = globalSearchData[category];
    if (isPropertyPlan) {
      return selectedKey.some((items: any) => isSamePropertyPlan(items, item));
    }
    const propertyToCheck = isCategoryProperty
      ? "innCode"
      : isCategoryUser
      ? "email"
      : isCategoryPlan
      ? "id"
      : isTemplatePlan
      ? "title"
      : null;

    if (!propertyToCheck) return false;
    return selectedKey.some((items: any) => items[propertyToCheck] === item[propertyToCheck]);
  };

  // Function to add a new item to recent searches based on category
  const addToRecentSearches = (category: string, item: any) => {
    const recentSearch = localStorage.getItem("recentSearch");
    if (recentSearch) {
      const globalSearchData = JSON.parse(recentSearch);
      const isDuplicate = findDuplicate(category, globalSearchData, item);

      // Create updated recent searches with the new item added to the specified category,
      // ensuring uniqueness and maintaining a maximum of 3 recent items per category
      if (!isDuplicate) {
        const updatedRecentSearches = {
          ...globalSearchData,
          [category]: [item, ...globalSearchData[category].filter((searchItem: any) => searchItem !== item)].slice(0, 3)
        };

        localStorage.setItem("recentSearch", JSON.stringify(updatedRecentSearches));
      }
    }
  };
  const lastIndexHltId = globalUserDetail[globalUserDetail.length - 1]?.hltId;

  // Function to handle scroll events and load more data if scrolled to the bottom of the list
  const onScroll = async () => {
    if (listInnerRef.current) {
      const mainDiv = document.getElementsByClassName("customAutoComplete")[0];
      const { scrollTop, scrollHeight, clientHeight } = mainDiv;

      // Check if scrolled to the bottom of the list
      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
        const isResultComplete = (pageNumber + 1) * resultPerPage < totalGlobalUser;

        if (isResultComplete && !globalUserListLoading) {
          if (isCategoryUser) userLoadMore();
        }
      }
    }
  };

  // Function to reset all data including page number, user list, search text,
  //and dispatching actions to clear search results and loading state
  const resetAllData = () => {
    setPageNumber(0);
    setUserList([]);
    setSearchText("");
    dispatch(clearSearch());
    dispatch(clearPlanSearch());
    dispatch(clearInnCodeSearch());
    dispatch(setTemplatePlanList([]));
    dispatch(setPropertyPlanList([]));
    dispatch(setPlanOwner([]));
    dispatch(setSearchLoadingCount(false));
  };

  const propertyAndTemplatePlanPayload = (planType: string) => {
    const payload: any = {
      payload: {
        includeFilters: true,
        includeOwners: true,
        planType: planType,
        searchText: searchText,
        functionalArea: "",
        innCode: planType === "PLAN" ? (isHotelUser ? innCode : isAbovePropertyUser ? getHltInncodes(true) : "") : ""
      },
      page: 0
    };
    if (planType !== "PLAN") payload.payload.searchedHltId = getUserHltId();
    return payload;
  };

  const multitargetApisPayload = () => {
    return {
      innCodes: isHotelUser ? [innCode] : getHltInncodes(),
      filter: {
        functionalAreaIds: [],
        filterIds: [],
        ids: []
      },
      sort: null,
      pagination: {
        page: 1,
        size: 100
      },
      searchText: searchText,
      planType: [0],
      isSortingDisabled: false
    };
  };

  // Debounced function to fetch global user data with a delay of 300 milliseconds
  const debouncedFetchGlobalData = _.debounce(async (data, planData) => {
    if (searchText.length > minSearchStrLength) {
      window.stop();
      if (isCategoryUser) await dispatch(fetchGlobalUser(data));
      if (isCategoryPlan) {
        if (isAbovePropertyUser || isHotelUser) {
          await dispatch(fetchMultiTargeted(multitargetApisPayload()));
        } else {
          await dispatch(fetchGlobalPlansAsync(planData));
        }
      }
      if (isCategoryProperty) {
        if (isAbovePropertyUser) {
          const innCodePayload = {
            inncodes: getHltInncodes(),
            propNameRequired: true,
            searchText: searchText
          };
          await dispatch(fetchInncodeInfo(innCodePayload));
        } else {
          await dispatch(
            fetchGlobalInncodes({
              searchText,
              isAbovePropertyUser,
              inncode: getHltInncodes(true)
            })
          );
        }
      }

      if (isTemplatePlan) {
        await dispatch(fetchTemplatePropertyPlansAsync(propertyAndTemplatePlanPayload("TEMPLATE_PLAN")));
      }
      if (isPropertyPlan) {
        await dispatch(fetchTemplatePropertyPlansAsync(propertyAndTemplatePlanPayload("PLAN")));
      }
    }
  }, 300);

  // useEffect hook to trigger fetching of global user, plan and Property data when search text changes
  useEffect(() => {
    const data = {
      page: 0,
      size: resultPerPage,
      value: searchText,
      includecorpusers: false,
      innCode: isHotelUser ? innCode : isAbovePropertyUser ? getHltInncodes() : ""
    };

    const planData = {
      filter: {
        innCode: selectedInncode,
        functionalAreaIds: [],
        filterIds: []
      },
      pagination: {
        page: 1,
        size: 100
      },
      searchText: searchText,
      planType: [allPlansType],
      sort: {
        property: "relevance",
        direction: "ASC"
      }
    };

    debouncedFetchGlobalData(data, planData);

    return () => {
      debouncedFetchGlobalData.cancel();
    };
  }, [searchText]);

  // Function to load more global user data
  const userLoadMore = async () => {
    setPageNumber(pageNumber + 1);
    const data = {
      page: pageNumber + 1,
      size: resultPerPage,
      value: searchText,
      includecorpusers: false
    };
    await dispatch(fetchGlobalUser(data));
  };

  // Function to render loader or display user data
  const renderLoader = () => {
    return (
      <div className="global-search-loader">
        {globalUserListLoading && <Spinner />}
        <div>
          <Typography color={tokens.Text.General.Color} variant={"label"} showBold>
            <Typography color={tokens.Text.General.Color} variant={"label"} className="pad-r-4">
              {translate("common.showing")}
            </Typography>
            {`${globalUserDetail.length} of ${totalGlobalUser} users`}
          </Typography>
        </div>
      </div>
    );
  };

  // Function to render data based on category
  const renderData = (props: any) => {
    if (isCategoryUser) {
      return userData(props);
    }

    if (isCategoryProperty) {
      return propertyData(props);
    }

    if (isCategoryPlan || isTemplatePlan) {
      return planData(props);
    }

    if (isPropertyPlan) {
      return propertyPlanData(props);
    }

    return [];
  };

  // Function to render property data
  const propertyData = (props: any) => {
    return (
      <div className="full-width property-view">
        <div className="info-wrapper">
          <div className="recent-search-wrapper">
            {isSearchTextEmpty && <Recent />}
            <Typography variant="label" color={tokens.Text.General.PrimaryBody} showBold className="ellipse-one">
              {props.innCode}
            </Typography>
          </div>
          <Typography
            variant="body2"
            color={tokens.Text.General.Secondary}
            className={`ellipse-two ${isSearchTextEmpty ? "pad-l-20" : ""}`}
          >
            {props.name}
          </Typography>
        </div>
      </div>
    );
  };

  const planData = (props: any) => {
    return (
      <div className="full-width plan-view">
        <div className="recent-search-plan">
          {isSearchTextEmpty && <Recent />}
          <Typography variant="body2" color={tokens.Text.General.Secondary} className="ellipse-two">
            {props.name}
          </Typography>
        </div>
      </div>
    );
  };

  const propertyPlanData = (props: any) => {
    const planOwners = planOwnerList.filter((data: any) => props.owners.includes(data.hltId));
    const owners = isSearchTextEmpty ? props.owners : planOwners;
    const names = owners?.map((item: any) => item.displayName).join(", ");
    return (
      <div className="full-width property-plan-view">
        <div className="recent-search-plan">
          {isSearchTextEmpty && <Recent />}
          <Typography variant="body2" color={tokens.Text.General.Secondary} className="ellipse-two">
            {props.name}
          </Typography>
        </div>
        <div className={`recent-search-plan ${isSearchTextEmpty ? "ml-20" : ""}`}>
          {isSearchTextEmpty && (
            <div className="visibility-hidden">
              <Recent />
            </div>
          )}
          <Typography variant="body2" color={tokens.Text.General.Secondary}>
            {names}
          </Typography>
        </div>
        <div className={`recent-search-plan ${isSearchTextEmpty ? "ml-20" : ""}`}>
          {isSearchTextEmpty && (
            <div className="visibility-hidden">
              <Recent />
            </div>
          )}
          <Typography variant="body2" color={tokens.Border.General.Secondary} className="ellipse-two">
            {props.innCode}
          </Typography>
        </div>
      </div>
    );
  };

  // Function to render user data
  const userData = (userInfo: any) => {
    const userType =
      userInfo.lobbyUserType.toLowerCase() === ABOVE_PROP_USER_TYPE
        ? translate("common.showing.abovePropertyMember")
        : translate("common.showing.onPropertyMember");
    return (
      <div className="full-width user-view">
        <div className="user-result">
          <div className="user-name">
            {isSearchTextEmpty && <Recent />}
            <Typography variant="body2" showBold color={tokens.Text.General.PrimaryBody} className="ellipse-two">
              {getUserNameWithConditionalCheck(userInfo?.displayName, userInfo?.firstName, userInfo?.lastName)}
            </Typography>
          </div>
          <div className={`${isSearchTextEmpty ? "pad-l-20" : ""}`}>
            <Typography variant="label" color={tokens.Text.General.Secondary}>
              {userType}
            </Typography>
            <div className="lower-content">
              {userInfo.email && (
                <Typography variant="body2" color={tokens.Text.General.Secondary} className="ellipse-one">
                  {userInfo.email}
                </Typography>
              )}
              <div className="user-inncode">
                <Typography variant="body2" color={tokens.Border.General.Secondary} className="ellipse-two">
                  {userInfo?.innCodes?.map((innCOde: string) => {
                    return (
                      <>
                        {innCOde}
                        <span className="comma-sepration">,</span>{" "}
                      </>
                    );
                  })}
                </Typography>
              </div>
            </div>
          </div>
          {lastIndexHltId === userInfo.hltId && renderLoader()}
        </div>
      </div>
    );
  };

  // Function to render option category based on data
  const renderOptionCategory = (data: any) => {
    const searchStrLower = searchText.toLocaleLowerCase();
    if (isCategoryPlan) {
      return data.name;
    }
    if (isTemplatePlan || isPropertyPlan) {
      if (data.title) {
        return "";
      }
    }
    if (isCategoryProperty) {
      const nameInLowerCase = data?.name?.toLocaleLowerCase();
      const innCodeInLowerCase = data?.innCode?.toLocaleLowerCase();
      if (nameInLowerCase?.includes(searchStrLower)) return data.name;
      if (innCodeInLowerCase?.includes(searchStrLower)) return data.innCode;
    }
    if (isCategoryUser) {
      const nameInLowerCase = getUserNameWithConditionalCheck(
        data?.displayName,
        data?.firstName,
        data?.lastName
      )?.toLocaleLowerCase();
      const emailInLowerCase = data?.email?.toLocaleLowerCase();
      if (nameInLowerCase?.includes(searchStrLower))
        return getUserNameWithConditionalCheck(data?.displayName, data?.firstName, data?.lastName);
      if (emailInLowerCase?.includes(searchStrLower)) return data.email;
    }
    return " ";
  };

  const addTorecentSearchval = (val: any) => {
    if (isPropertyPlan) {
      const planOwners = planOwnerList.filter((data: any) => val.owners.includes(data.hltId));
      const res = {
        ...val,
        owners: planOwners
      };
      return res;
    }
    return val;
  };

  const redirectToPage = (value: any) => {
    const modifiedVal = addTorecentSearchval(value);
    const properties = value?.properties;
    if (!isHotelUser && !isImpersonated) {
      addToRecentSearches(category, modifiedVal);
    }
    if (isCategoryProperty) navigate(`/property/${value?.innCode}?parentTab=Property+Plans`);
    if (isCategoryUser) {
      const userInncode = isCorp || isConsultant || isAbovePropertyUser ? value?.innCodes?.[0] : innCode;
      navigate(`/userview/${value?.hltId}?inncode=${userInncode}`);
    }
    if (isCategoryPlan) {
      const planInncode = isCorp || isConsultant ? DEFAULT_CORP_INNCODE : isAbovePropertyUser ? properties[0] : innCode;
      navigate(`/plandetails/${value.id}`, {
        state: {
          innCode: planInncode
        }
      });
    }
    if (isTemplatePlan) navigate(`/templateplandetails/${value.id}`);
    if (isPropertyPlan) navigate(`/myplandetails/${value.id}`);
  };

  // Function to render options based on search text and category
  const renderOptions = () => {
    const optionMap: any = {
      Member: globalUserDetail,
      "Library Plan": globalPlanList,
      Property: globalInncodeList,
      "Template Plan": templatePlanList,
      "Property Plan": propertyPlanList
    };
    if ((isImpersonated && isEmpty(searchText)) || (isHotelUser && isEmpty(searchText))) {
      return [];
    }
    if (isEmpty(searchText)) {
      const searchResult = localStorage.getItem("recentSearch");
      return searchResult ? JSON.parse(searchResult)?.[category] ?? [] : [];
    }
    return optionMap[category] ?? [];
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = () => {
    setIsOpen(false);
  };

  const recentSearch = (info: any) => {
    if (isCategoryProperty) {
      return (
        <div className="recentSearch-user">
          {recentFirstElement()?.innCode === info.innCode && (
            <Typography color={tokens.Border.General.Secondary} variant="label">
              {translate("globalSearch.recentProperty.search")}
            </Typography>
          )}
        </div>
      );
    } else if (isCategoryUser) {
      return (
        <>
          {isSearchTextEmpty && (
            <div className="recentSearch-user">
              {recentFirstElement()?.email === info.email && (
                <Typography color={tokens.Border.General.Secondary} variant="label">
                  {translate("globalSearch.recentUser.search")}
                </Typography>
              )}
            </div>
          )}
        </>
      );
    } else {
      const firstRecent = recentFirstElement();
      const isFirstRecent = isPropertyPlan ? isSamePropertyPlan(firstRecent, info) : firstRecent?.name === info.name;
      return (
        <div className="recentSearch-plan">
          {isFirstRecent && (
            <Typography color={tokens.Border.General.Secondary} variant="label">
              {translate("globalSearch.recentPlan.search")}
            </Typography>
          )}
        </div>
      );
    }
  };

  const autoCompleteOptions = isUndefined(renderOptions()) ? [] : renderOptions();
  return (
    <div className="global-search-component">
      <div className="main-container">
        <div className="auto-complete-wrapper">
          <Autocomplete
            popupIcon=""
            className="global-autComplete"
            ref={listInnerRef}
            filterOptions={(option) => option}
            ListboxProps={{
              className: "customAutoComplete",
              onScroll: (event) => {
                onScroll();
              }
            }}
            onChange={(_, newValue: string | null) => {
              redirectToPage(newValue);
            }}
            onScroll={onScroll}
            loading={searchLoading}
            loadingText={
              <Typography variant="label" color={tokens.Text.General.Secondary}>
                Loading...
              </Typography>
            }
            freeSolo
            options={autoCompleteOptions?.map((option: any) => {
              return option;
            })}
            getOptionLabel={(user: any) => {
              return renderOptionCategory(user);
            }}
            onInputChange={handleInputChange}
            inputValue={searchText}
            open={searchText.length > 2 || isOpen}
            renderInput={(params) => (
              <TextField
                onFocus={handleFocus}
                onBlur={handleBlur}
                className="autocomplete-textField"
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment sx={{ paddingLeft: "6px" }} position="start">
                        <SearchIcon />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                    }
                  }
                }}
              />
            )}
            renderOption={(props, option: any) => {
              return (
                <>
                  {recentSearch(option)}
                  <li {...props}>{renderData(option)}</li>
                </>
              );
            }}
          />
        </div>
        <div className="category-select-wrapper">
          <FormControl>
            <Select
              IconComponent={ArrowDown}
              value={category}
              onChange={handleCategoryChange}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              className="category-select"
              autoWidth={false}
            >
              {categoryData(isConsultant, isCorp, isAbovePropertyUser).map((category: string) => {
                return (
                  <MenuItem value={category}>
                    <Typography variant="body2" color={tokens.Text.General.Secondary}>
                      {category}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
